<script setup lang="ts">
import { useIsSubdomain } from '~/composables/useSubdomain'

const { t: $t } = useI18n()

export interface INavLinkItem {
  path: string
  text: string
  /* whether to show this nav item in  */
  showOnSub?: boolean
}

defineEmits(['navbarClose'])

withDefaults(
  defineProps<{
    isOpen: boolean
    navLinks: INavLinkItem[]
  }>(),
  { isOpen: false }
)

const isSubdomain = useIsSubdomain()

function setHoverOrigin(e: MouseEvent) {
  const el = e.currentTarget as HTMLElement
  if (el.dataset.originLocked === 'true') return

  const rect = el.getBoundingClientRect()
  const x = e.clientX - rect.left
  const third = rect.width / 3

  if (x < third) {
    el.style.setProperty('--hover-origin', 'left')
  } else if (x > rect.width - third) {
    el.style.setProperty('--hover-origin', 'right')
  } else {
    el.style.setProperty('--hover-origin', 'center')
  }

  el.dataset.originLocked = 'true'
}

function resetHoverOrigin(e: MouseEvent) {
  const el = e.currentTarget as HTMLElement
  // Waiting for animation end (same duration as in css)
  setTimeout(() => {
    el.dataset.originLocked = 'false'
    el.style.removeProperty('--hover-origin')
  }, 300) // 300ms = transition-duration in css
}
</script>
<template>
  <nav
    id="navbar-sticky"
    class="navbar-sticky"
    :class="[isOpen ? 'navbar-sticky--open' : '']"
  >
    <div
      class="max-w-screen-xl px-4 py-4 md:py-1 mx-auto h-full flex flex-col overflow-auto"
    >
      <div>
        <span
          class="inline-block text-3xl font-thin rotate-45 mb-4 md:hidden text-teal-900"
          @click="$emit('navbarClose')"
          >+</span
        >
      </div>
      <div class="flex flex-col md:flex-row md:items-center md:justify-between">
        <ul
          class="flex flex-col md:flex-row font-medium mt-0 space-y-4 md:space-y-0 md:space-x-8 rtl:space-x-reverse text-sm"
        >
          <li
            v-for="item in navLinks"
            :key="item.path"
            class="border-b pb-4 md:border-none md:pb-0"
          >
            <NuxtLinkLocale
              class="text-stone-800 text-sm font-normal navigation-link"
              :to="item.path"
              @click="$emit('navbarClose')"
              @mouseenter="setHoverOrigin"
              @mouseleave="resetHoverOrigin"
            >
              {{ $t(item.text) }}
            </NuxtLinkLocale>
          </li>
        </ul>
        <TeamEventRequestButton v-if="!isSubdomain" class="hidden sm:block"
          >{{ $t('home.headerButtonTitle') }}
        </TeamEventRequestButton>
      </div>
      <LocaleSwitcher @navbar-close="$emit('navbarClose')" />
      <MinimalContactInfo />
    </div>
  </nav>
</template>

<style lang="postcss" scoped>
.navbar-sticky {
  @apply fixed left-0 top-0 md:left-auto md:relative w-full h-[100dvh] md:h-fit bg-white hidden md:block border-b border-gray-200;

  &--open {
    @apply block;
  }
}

.router-link-active {
  @apply font-semibold underline;
}

.navigation-link {
  @apply relative inline-block no-underline;

  &::after {
    content: '';
    position: absolute;
    bottom: -10px;
    left: 0;
    width: 100%;
    height: 2px;
    background-color: currentColor;
    transform: scaleX(0);
    transform-origin: var(--hover-origin, center);
    transition:
      transform 0.3s ease,
      transform-origin 0.1s ease;
  }

  &.router-link-exact-active,
  &:hover {
    &::after {
      transform: scaleX(1);
    }
  }
}
</style>
